import { useEffect, useRef } from 'react';
import { Logo } from '../components/Logo';

/**
 * Google sign-in gate. Renders the GSI button (shared web client id) and hands
 * the returned credential up to useAuth().loginWithGoogle, which exchanges it
 * at staff-api /auth/token. Only shown when VITE_STAFF_AUTH_DISABLED !== 'true'
 * (App decides). If the client id is unset we surface a config hint rather than
 * a blank card.
 */
declare global {
  interface Window {
    google?: any;
  }
}

const CLIENT_ID = import.meta.env.VITE_STAFF_SSO_GOOGLE_CLIENT_ID || '';

export function LoginPage({
  onCredential,
}: {
  onCredential: (idToken: string) => Promise<void>;
}) {
  const btnRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!CLIENT_ID) return;
    let resizeObserver: ResizeObserver | undefined;
    let disposed = false;
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.onload = () => {
      if (disposed) return;
      window.google?.accounts.id.initialize({
        client_id: CLIENT_ID,
        callback: (resp: { credential: string }) => {
          void onCredential(resp.credential);
        },
      });
      const container = btnRef.current;
      if (container) {
        let previousWidth = 0;
        const renderButton = () => {
          const width = Math.min(400, Math.floor(container.clientWidth));
          if (width <= 0 || width === previousWidth) return;
          previousWidth = width;
          container.replaceChildren();
          window.google?.accounts.id.renderButton(container, {
            theme: 'filled_black',
            size: 'large',
            shape: 'rectangular',
            width,
          });
        };
        renderButton();
        resizeObserver = new ResizeObserver(renderButton);
        resizeObserver.observe(container);
      }
    };
    document.body.appendChild(script);
    return () => {
      disposed = true;
      resizeObserver?.disconnect();
      script.remove();
    };
  }, [onCredential]);

  return (
    <main className="login-wrap">
      <section className="login-card" aria-labelledby="login-title">
        <div className="login-brand">
          <Logo size={32} />
        </div>
        <h1 id="login-title" className="login-title">
          Sign in to your workspace
        </h1>
        <p className="login-lede">
          Manage OptiSigns repositories, clusters, and infrastructure access in
          one place.
        </p>
        {CLIENT_ID ? (
          <div className="login-action">
            <p className="login-account-note">
              Use your <strong>@optisigns.com</strong> Google account.
            </p>
            <div ref={btnRef} className="login-google-button" />
          </div>
        ) : (
          // Config hint for a developer, not for a customer: this branch only
          // renders when the build has no client id, which never happens in a
          // deployed environment.
          <p className="login-config-note">
            Google sign-in isn&apos;t configured for this build. For local dev
            set <code>VITE_STAFF_AUTH_DISABLED=true</code>, or set{' '}
            <code>VITE_STAFF_SSO_GOOGLE_CLIENT_ID</code>.
          </p>
        )}
      </section>
    </main>
  );
}
